/**
 * The events of a session on the person's own computer, as the controller
 * folds them. The cloud server wraps each OpenCode event in the `agent`
 * envelope, numbers it per session, and adds the `turn` and `session_status`
 * events that move turn activity. A local session has no server, so this
 * page numbers OpenCode's raw stream itself, the stream the page keeps to
 * its server (`runEventStream`): one `LocalSessionFeed` per session numbers
 * every event it sees, starting with the raw tail the page's own snapshot
 * carries, so text in progress survives a reload, and the numbers
 * stay contiguous for as long as the page keeps the session's controller. The
 * page's connection feeds every opened session from the stream
 * (`feedLocalSessions`), view or no view, so a view opened again finds the
 * log whole; a stream that lost events loads each opened session again. It
 * adds the session's status as an execution starts and ends. Each turn's
 * status has one source, the rows a read builds from OpenCode's messages and
 * inbox (`localTurnRows`): an inbox event on the stream asks the controller
 * for the next read, and the shared turn fold reads a delivery or a cancel
 * from the relayed event until it lands. A send OpenCode took and never
 * finished answering is settled by the first read begun after it gave up:
 * listed, it is OpenCode's row; not listed, OpenCode never got it, and the
 * read carries it as a failed send (`unsentBefore`).
 */
import type { TailSignal } from '@/lib/code-sessions/local-agent/event-tail';
import type { OpenCodeEvent } from '@/lib/code-sessions/local-agent/opencode-api';
import type { CodeSessionEvent } from '@/lib/code-sessions/types';

import type { CodePromptAttachment, CodeTurnDelivery } from '../../types';
import type { SessionFeedController } from '../types';
import type { LocalTurnRow, NumberedTail } from './local-snapshot';

import { UnconfirmedSends } from '../unconfirmed-sends';
import { NEVER_GOT_MESSAGE } from './local-errors';
import { isoTime, localPromptId, sentTurn } from './local-snapshot';

import { EVENT_TAIL_CAPACITY } from '@/lib/code-sessions/local-agent/event-tail';
import { sessionIdOf } from '@/lib/code-sessions/local-agent/opencode-api';

/**
 * The events the cloud relays besides OpenCode's durable log: the deltas and
 * the interactive forms. A local session adds the permission requests, which
 * the cloud never has because its sandbox asks nobody.
 */
const RELAYED_VOLATILE_TYPES =
  /^(?:session\.text\.delta|session\.reasoning\.delta|session\.tool\.input\.delta|session\.tool\.progress|form\.|permission\.)/;

/** The events that end an execution; how each of its turns ended is in the rows of the read that follows. */
const EXECUTION_END_TYPES = new Set([
  'session.execution.succeeded',
  'session.execution.failed',
  'session.execution.interrupted',
]);

/** Whether the page keeps an event: what the cloud relays, with a local session's permission requests. */
export function isRelayedLocalEvent(event: OpenCodeEvent): boolean {
  if (event.type.startsWith('session.revert.')) {
    return false;
  }
  return 'durable' in event || RELAYED_VOLATILE_TYPES.test(event.type);
}

export type LocalFeedListener = (events: CodeSessionEvent[]) => void;

/**
 * What a feed asks of its session's controller: to take events while no view listens, to load
 * again, and to read the session again for a permission request.
 */
type FeedController = Pick<SessionFeedController, 'receive' | 'resync' | 'refresh'>;

/**
 * How long token deltas wait for the controller while no view listens, as a mounted view's sync
 * holds them (`session-sync.tsx`): each `receive` copies the session's whole log, so it takes the
 * deltas of a frame together, not one per token.
 */
const HELD_DELTA_MS = 32;

function isTokenDelta(event: CodeSessionEvent): boolean {
  return event.kind === 'agent' && event.type.endsWith('.delta');
}

export class LocalSessionFeed {
  private nextSeq = 1;
  /** The session's controller once a view opened it; takes the stream while no view listens. */
  private controller: FeedController | null = null;
  /** Numbered events held for the controller while no view listens, and the timer that hands them over. */
  private held: CodeSessionEvent[] = [];
  private heldTimer: ReturnType<typeof setTimeout> | undefined;
  /** Each raw event's numbered events, by the raw event's id, oldest first. */
  private converted = new Map<string, CodeSessionEvent[]>();
  /** Live events that arrived before the first snapshot placed the numbering. */
  private buffered: OpenCodeEvent[] = [];
  private numbered = false;
  private readonly listeners = new Set<LocalFeedListener>();
  /** What each mounted view drops when the stream lost events: the deltas its sync still holds. */
  private readonly droppers = new Set<() => void>();
  /** The last read's rows, and the prompts this page sent since, by turn id. */
  private rows = new Map<string, LocalTurnRow>();
  /** The revision at which this page sent each prompt no read has placed yet, by turn id. */
  private sentAt = new Map<string, number>();
  /**
   * The sends OpenCode took and never finished answering that no read has listed, each as a
   * failed send, with the clock that counts this page's sends and resets, so a read's checkpoint
   * says which sends it began before.
   */
  private unconfirmed = new UnconfirmedSends<LocalTurnRow>();

  constructor(readonly sessionId: string) {}

  /**
   * Listens for the numbered events of raw events that arrive live, and, through `drop`, for a
   * stream that lost events, before the numbering goes.
   */
  subscribe(listener: LocalFeedListener, drop?: () => void): () => void {
    // What streamed while no view listened reaches the controller before the view's own events.
    this.flushHeld();
    this.listeners.add(listener);
    if (drop) {
      this.droppers.add(drop);
    }
    return () => {
      this.listeners.delete(listener);
      if (drop) {
        this.droppers.delete(drop);
      }
    };
  }

  /**
   * Hands the feed its session's controller, as a view opens the session; the latest wins, and
   * it stays until the feed is forgotten with the controller (`forgetLocalSessionFeed`).
   */
  attach(controller: FeedController) {
    this.controller = controller;
  }

  /** The last row this page built for a turn, which a steer or a cancel returns changed. */
  rowOf(turnId: string): LocalTurnRow | undefined {
    return this.rows.get(turnId);
  }

  /** Marks the live state before a snapshot read begins. */
  checkpoint(): number {
    return this.unconfirmed.checkpoint();
  }

  /**
   * A prompt this page sent: the row a read already built for it, when the
   * stream's own events outran the POST's response, else a queued row placed
   * after every turn the page knows, with the images it carries. The send is
   * recorded, so a read that began before it keeps the row (`sentSince`).
   * OpenCode answered it, so an earlier send of the same turn that it never
   * answered stands as sent.
   */
  sent(input: {
    turnId: string;
    text: string;
    delivery: CodeTurnDelivery;
    createdAt: number;
    attachments: readonly CodePromptAttachment[];
  }): LocalTurnRow {
    this.sentAt.set(input.turnId, this.unconfirmed.bump());
    this.unconfirmed.drop(input.turnId);
    const known = this.rows.get(input.turnId);
    if (known !== undefined) {
      return known;
    }
    const row = sentTurn({
      turnId: input.turnId,
      messageId: localPromptId(input.turnId),
      text: input.text,
      delivery: input.delivery,
      index: this.nextIndex(),
      createdAt: input.createdAt,
      attachments: input.attachments,
    });
    this.rows.set(row.id, row);
    return row;
  }

  /**
   * A send OpenCode took and never finished answering, which it may hold or
   * never have got. A read that began while the send was on its way may have
   * looked before OpenCode saved it, so only a read begun after this settles
   * it (`unsentBefore`). As a failed send it keeps the place the person sent
   * it in, and the images, which its Retry sends again, and it ends where it
   * was sent: it never ran, so its error stays under its prompt, and nothing
   * the agent took while it waited joins it.
   */
  unconfirmedSend(input: {
    turnId: string;
    text: string;
    delivery: CodeTurnDelivery;
    createdAt: number;
    attachments: readonly CodePromptAttachment[];
  }) {
    const sent = sentTurn({
      turnId: input.turnId,
      messageId: localPromptId(input.turnId),
      text: input.text,
      delivery: input.delivery,
      index: this.nextIndex(),
      createdAt: input.createdAt,
      attachments: input.attachments,
    });
    this.unconfirmed.add(input.turnId, {
      ...sent,
      status: 'failed',
      endedAt: isoTime(input.createdAt),
      errorMessage: NEVER_GOT_MESSAGE,
      unsent: true,
    });
  }

  /**
   * Numbers a snapshot's raw tail for this page, and takes the read's rows as
   * the turns the page knows. A send the read began after stands as the read
   * says: listed, or gone because OpenCode delivered or removed it. A send
   * after the read began is in no inbox the read saw, so its row stays until
   * a later read places it. Reads prime one at a time: the query dedupes
   * them and a cancelled one aborts before it primes; `rowFor`'s own read,
   * for a row the feed lacks, is the one that can overlap, and the next read
   * replaces what it placed. The first snapshot places the numbering: live
   * events that arrived before it are delivered after it, less the ones the
   * tail holds. A later snapshot keeps every number it gave before. A send
   * OpenCode never answered that the read lists reached it after all,
   * whenever the read began.
   */
  prime(
    tail: readonly OpenCodeEvent[],
    turns: readonly LocalTurnRow[],
    checkpoint = this.unconfirmed.checkpoint(),
  ): NumberedTail {
    const before = this.rows;
    this.rows = new Map(turns.map((turn) => [turn.id, turn]));
    this.unconfirmed.settle(this.rows.keys());
    for (const [id, at] of this.sentAt) {
      if (at <= checkpoint) {
        this.sentAt.delete(id);
        continue;
      }
      const row = before.get(id);
      if (row !== undefined && !this.rows.has(id)) {
        this.rows.set(id, row);
      }
    }
    const events = tail.filter(isRelayedLocalEvent).flatMap((raw) => this.convert(raw));
    const cursor = this.nextSeq - 1;
    if (!this.numbered) {
      this.numbered = true;
      const held = this.buffered;
      this.buffered = [];
      const extra = held
        .filter(isRelayedLocalEvent)
        .flatMap((raw) => (this.converted.has(raw.id) ? [] : this.convert(raw)));
      if (extra.length > 0) {
        this.notify(extra);
      }
    }
    return { events, cursor };
  }

  /** This page's sends since `checkpoint` that `listed` lacks, as their rows. */
  sentSince(checkpoint: number, listed: ReadonlySet<string>): LocalTurnRow[] {
    return [...this.sentAt].flatMap(([id, at]) => {
      const row = this.rows.get(id);
      return at > checkpoint && !listed.has(id) && row !== undefined ? [row] : [];
    });
  }

  /**
   * The sends OpenCode never got, for a read begun at `checkpoint` that
   * `prime` took: each one that gave up before the read began and that the
   * read does not list, as a failed send that keeps its text.
   */
  unsentBefore(checkpoint: number): LocalTurnRow[] {
    return this.unconfirmed.before(checkpoint);
  }

  /** A raw event that arrived live. */
  push(raw: OpenCodeEvent) {
    if (!this.numbered) {
      this.buffered.push(raw);
      if (this.buffered.length > EVENT_TAIL_CAPACITY) {
        this.buffered.shift();
      }
      return;
    }
    if (this.converted.has(raw.id)) {
      return;
    }
    this.notify(this.convert(raw));
  }

  /**
   * Forgets every number and every turn, for a stream that lost events: the
   * next snapshot places the numbering again, as a reload would. The sends
   * OpenCode never answered stay: whether it got them is not the stream's to
   * say, and the controller drops their rows with the log once the sends gave
   * up, so the next read carries the ones OpenCode never got.
   */
  reset() {
    this.nextSeq = 1;
    this.converted = new Map();
    this.buffered = [];
    this.numbered = false;
    this.rows = new Map();
    this.sentAt = new Map();
    this.unconfirmed.bump();
    // What is held carries the numbers that just went.
    this.dropHeld();
  }

  /**
   * An event of this session on the page's stream, once a view opened the session: numbered, for
   * the view's sync or, while the view is away, the controller.
   */
  streamed(event: OpenCodeEvent) {
    if (this.controller === null) {
      return;
    }
    // A permission request of this session: the session is read again, and the read carries
    // every request the consent guard may answer, so the card comes with it; a read begun before
    // the request may lack it, so it gives way. A request answered before the read returns, as a
    // remembered command's is, shows no card: its reply ends it in the log, after the read's
    // forms. The agent waits on the request and sends nothing more, so a read that fails says why
    // with Retry (`SessionTranscriptStatus`), whose Retry starts the server again and loads it.
    if (event.type === 'permission.asked') {
      void this.controller.refresh({ cancelRefetch: true });
    }
    if (isRelayedLocalEvent(event)) {
      this.push(event);
    }
  }

  /**
   * The stream lost events, once a view opened the session: what the views hold of the old log
   * goes, then the numbering and the turns (`reset`), and the controller loads the session
   * again, as a reload would.
   */
  lost() {
    if (this.controller === null) {
      return;
    }
    this.droppers.forEach((drop) => drop());
    this.reset();
    this.controller.resync();
  }

  /**
   * The numbered events go to the listeners, a mounted view's sync, which coalesces the deltas;
   * while none listens, to the controller, token deltas a frame at a time (`HELD_DELTA_MS`) and
   * every other event at once, with the deltas held before it.
   */
  private notify(events: CodeSessionEvent[]) {
    if (this.listeners.size > 0) {
      for (const listener of this.listeners) {
        listener(events);
      }
      return;
    }
    this.held.push(...events);
    if (!events.every(isTokenDelta)) {
      this.flushHeld();
    } else if (this.heldTimer === undefined) {
      this.heldTimer = setTimeout(() => this.flushHeld(), HELD_DELTA_MS);
    }
  }

  private flushHeld() {
    const events = this.held;
    this.dropHeld();
    if (events.length > 0) {
      this.controller?.receive(events);
    }
  }

  private dropHeld() {
    clearTimeout(this.heldTimer);
    this.heldTimer = undefined;
    this.held = [];
  }

  private take(): number {
    const seq = this.nextSeq;
    this.nextSeq += 1;
    return seq;
  }

  /**
   * The place after every turn the page knows, where it puts a prompt it
   * sends. A send OpenCode never got keeps its place, so a prompt sent after
   * it goes after it.
   */
  private nextIndex(): number {
    let next = 0;
    for (const row of this.rows.values()) {
      next = Math.max(next, row.index + 1);
    }
    for (const row of this.unconfirmed.rows()) {
      next = Math.max(next, row.index + 1);
    }
    return next;
  }

  private convert(raw: OpenCodeEvent): CodeSessionEvent[] {
    const known = this.converted.get(raw.id);
    if (known !== undefined) {
      return known;
    }
    const at = 'created' in raw ? raw.created : Date.now();
    const opencodeSeq = 'durable' in raw ? raw.durable.seq : undefined;
    const agent: CodeSessionEvent = {
      v: 1,
      seq: this.take(),
      at,
      kind: 'agent',
      type: raw.type,
      ...(opencodeSeq === undefined ? {} : { opencodeSeq }),
      data: raw.data,
    };
    const events = [agent, ...this.derive(raw, at)];
    this.converted.set(raw.id, events);
    if (this.converted.size > EVENT_TAIL_CAPACITY) {
      const oldest = this.converted.keys().next().value;
      if (oldest !== undefined) {
        this.converted.delete(oldest);
      }
    }
    return events;
  }

  /**
   * The events the cloud server would add around a raw event: the session's
   * status as an execution starts and ends, and the end of a permission card
   * once its request is answered. What each turn became is in the rows of the
   * read that follows, which the controller asks for.
   */
  private derive(raw: OpenCodeEvent, at: number): CodeSessionEvent[] {
    if (EXECUTION_END_TYPES.has(raw.type)) {
      return [{ v: 1, seq: this.take(), at, kind: 'session_status', status: 'idle' }];
    }
    switch (raw.type) {
      case 'session.execution.started':
        return [{ v: 1, seq: this.take(), at, kind: 'session_status', status: 'running' }];
      case 'permission.asked':
        // The card comes from the consent guard's approvals, read fresh by the sync: the raw
        // request lacks the whole command and the folder the guard keys its answer on.
        return [];
      case 'permission.replied':
        return [
          {
            v: 1,
            seq: this.take(),
            at,
            kind: 'agent',
            type: 'form.cancelled',
            data: { id: raw.data.requestID, sessionID: raw.data.sessionID },
          },
        ];
      default:
        return [];
    }
  }
}

const feeds = new Map<string, LocalSessionFeed>();

/** The page's feed for a session; one per session for the life of its controller. */
export function localSessionFeed(sessionId: string): LocalSessionFeed {
  let feed = feeds.get(sessionId);
  if (feed === undefined) {
    feed = new LocalSessionFeed(sessionId);
    feeds.set(sessionId, feed);
  }
  return feed;
}

/** Drops a session's feed with its controller, so a later visit numbers from a fresh snapshot. */
export function forgetLocalSessionFeed(sessionId: string) {
  feeds.delete(sessionId);
}

/**
 * The page's connection relays its stream here (`connection.ts`). An event of an opened session,
 * one whose feed has a controller, is numbered and goes to the view's sync or, while the view is
 * away, to the controller. A session no view opened has no controller, so nothing buffers for
 * it. A stream that lost events loads every opened session again (`lost`).
 */
export function feedLocalSessions(signal: TailSignal): void {
  if (signal.kind === 'resync') {
    feeds.forEach((feed) => feed.lost());
    return;
  }
  const sessionId = sessionIdOf(signal.event);
  if (sessionId !== null) {
    feeds.get(sessionId)?.streamed(signal.event);
  }
}
